import { MapPinIcon } from "lucide-react";
import { useState } from "react";
import { useWatch, type UseFormReturn } from "react-hook-form";

import { Button } from "@/components/ui/button";
import { TableCell, TableRow } from "@/components/ui/table";

import type { InformeValues } from "./InformeFormulario";
import { BotonDescartarDeTerreno } from "./DescarteDeTerreno";
import { momentoDeSincronizacion } from "./registroDeTerreno";
import {
  cambiosDeLaPropuesta,
  textoDeLaPropuesta,
  valoresDeFila,
  yaEstaEnLosRoles,
  type PropuestaDeRol,
  type RolNuevoDeTerreno,
  type UnidadDelInforme,
  type ValoresDeRol,
} from "./rolesTerreno";

/* ---------------------------------------------------------------------------
   Lo que la App de terreno propone en la tabla "Roles del informe".

   Las dos piezas SOLO ofrecen: "Usar datos de terreno" y "Agregar a los roles"
   cambian el formulario como si la persona hubiera tecleado, y guardarlo es
   un acto aparte ("Guardar cambios"). Misma regla que "Usar ubicación de
   terreno".
--------------------------------------------------------------------------- */

function autoria(por: string | null, cuando: string | null): string | null {
  const momento = cuando ? momentoDeSincronizacion(cuando) : null;

  return [por, momento && momento !== "—" ? momento : null].filter(Boolean).join(", ") || null;
}

/**
 * La línea bajo una fila de la tabla cuando el encargado propuso otros valores
 * para ese rol. Se esconde sola cuando la fila ya los tiene (aplicados con el
 * botón o tecleados a mano).
 */
export function LineaDeRolDeTerreno({
  form,
  indice,
  propuesta,
  unidad,
  columnas = 5,
}: {
  form: UseFormReturn<InformeValues>;
  indice: number;
  propuesta: PropuestaDeRol;
  unidad: UnidadDelInforme;
  columnas?: number;
}) {
  const base = `roles.${indice}` as const;
  const fila = useWatch({ control: form.control, name: base });

  if (!fila) return null;

  const cambios = cambiosDeLaPropuesta(propuesta.datos, fila, unidad);
  const texto = textoDeLaPropuesta(propuesta.datos, cambios);

  if (Object.keys(cambios).length === 0 || texto === "") return null;

  function usar() {
    for (const [campo, valor] of Object.entries(cambios) as [keyof ValoresDeRol, string][]) {
      form.setValue(`${base}.${campo}`, valor, { shouldDirty: true, shouldValidate: true });
    }
  }

  const autor = autoria(propuesta.por, propuesta.capturadaEn);

  return (
    <TableRow className="hover:bg-transparent">
      <TableCell colSpan={columnas} className="pt-0 pb-3">
        <div className="border-success-dot/40 bg-success-soft/40 flex flex-wrap items-center gap-3 rounded-xl border-[1.5px] px-3.5 py-2">
          <MapPinIcon
            className="text-success-soft-foreground size-[1.0625rem] flex-none"
            aria-hidden="true"
          />
          <p className="text-ink-body min-w-0 flex-1 text-xs font-semibold">
            <span className="text-ink font-bold">En terreno: {texto}</span>
            {autor ? <span className="text-ink-muted font-mono"> · {autor}</span> : null}
          </p>
          <Button type="button" variant="secondary" size="sm" onClick={usar}>
            Usar datos de terreno
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

/**
 * Los roles que el encargado creó en el predio y la tabla todavía no tiene.
 * Agregarlos copia sus valores a una fila nueva (sin `id`: el servidor la
 * crea al guardar).
 */
export function RolesAgregadosEnTerreno({
  nuevos,
  filas,
  unidad,
  onAgregar,
}: {
  nuevos: RolNuevoDeTerreno[];
  filas: Pick<ValoresDeRol, "rol_sii" | "comuna_codigo">[];
  unidad: UnidadDelInforme;
  onAgregar: (valores: ValoresDeRol) => void;
}) {
  // Un rol de terreno SIN número no se reconoce en ninguna fila: sin esta
  // memoria, agregarlo dos veces dejaría dos filas vacías idénticas.
  const [agregados, setAgregados] = useState<ReadonlySet<string>>(new Set());

  if (nuevos.length === 0) return null;

  return (
    <div className="border-border bg-superficie-sutil space-y-2 rounded-xl border-[1.5px] px-3.5 py-3">
      <div className="text-ink text-[0.78125rem] font-bold">Roles agregados en terreno</div>
      <ul className="space-y-2">
        {nuevos.map((nuevo) => {
          const completo = textoDeLaPropuesta(nuevo.datos, {
            rol_sii: "",
            comuna_codigo: "",
            superficie: "",
            superficie_construida: "",
          });
          const titulo =
            nuevo.datos.rol_sii && nuevo.datos.rol_sii.trim() !== "" ? completo : `Rol sin número${completo ? ` · ${completo}` : ""}`;
          const incluido =
            agregados.has(nuevo.referencia) || yaEstaEnLosRoles(nuevo.datos, filas);

          return (
            <li key={nuevo.referencia} className="flex flex-wrap items-center gap-3">
              <p className="text-ink-body min-w-0 flex-1 text-xs font-semibold">
                <span className="text-ink font-bold">{titulo}</span>
                {nuevo.por ? <span className="text-ink-muted font-mono"> · {nuevo.por}</span> : null}
              </p>
              <BotonDescartarDeTerreno
                uuid={nuevo.referencia.slice(nuevo.referencia.lastIndexOf(":") + 1)}
                etiqueta={`rol agregado en terreno · ${titulo}`}
              />
              {incluido ? (
                <span className="text-ink-muted text-xs font-bold">Ya está en los roles</span>
              ) : (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    onAgregar(valoresDeFila(nuevo.datos, unidad));
                    setAgregados((actual) => new Set(actual).add(nuevo.referencia));
                  }}
                >
                  Agregar a los roles
                </Button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
